/* eslint-disable react/display-name, @typescript-eslint/no-empty-function */
/*
 * Copyright © 2020-2023 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: jiakaiwen jiakaiwen@huihaiyinhe.com
 * @LastEditTime: 2025-10-24 20:02:41
 */
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Input } from 'antd'
import { useAppSelector, useVip } from '../../../app/hooks'
import sVipGroupBadge from '../../../images/header/svip_group.png'
import vipGroupBadge from '../../../images/header/vip_group.png'
import vVipGroupBadge from '../../../images/header/vVip_group.png'
import notVipGroupBadge from '../../../images/header/not_vip_group.png'
import vipGroupIcon from '../../../images/crown-vip-icon.png'
import sVipGroupIcon from '../../../images/crown-svip-icon.png'
import vVipGroupIcon from '../../../images/crown-vVip-icon.png'
import groupSelected from '../../../images/header/group_selected.png'
import sensors from '../../../utils/sensors'
import CONFIG from '../../../constants/config'
import { pauseAllDownloadingTasks } from '../../../utils/downloadTaskManager'
import { DownOutlined, SearchOutlined } from '@ant-design/icons'

const PERSONAL_SPACE_PATH = '/my-space/personal-space'

const isOnPersonalSpace = () => window.location.pathname.startsWith('/my-space')

const GroupAvatar = ({ avatar, name, color, className }) => {
  if (avatar && avatar !== '') {
    return <img className={className} src={avatar} alt="" />
  } else {
    return (
      <div className={className} style={{ backgroundColor: color }}>
        {name.substr(0, 2)}
      </div>
    )
  }
}

interface ChooseGroupProps {
  visible: boolean
  onClickJoin?: () => void
  onClickCreate?: () => void
}

const ChooseGroup: React.FC<ChooseGroupProps> = ({
  visible,
  onClickJoin,
  onClickCreate
}) => {
  const groups = useAppSelector((state) => state.main.user?.groups)
  const curGroup = useAppSelector((state) => state.group)
  const nickname = useAppSelector((state) => state.main.user?.nickname)
  const isSpaceOpen = useAppSelector(
    (state: any) => state.mySpace?.personalSpace?.isSpaceOpen ?? false
  )
  const [groupName, setGroupName] = useState('')
  const [isFocused, setIsFocused] = useState(false)
  const blurTimerRef = useRef<number | null>(null)

  const cancelBlurTimer = () => {
    if (blurTimerRef.current !== null) {
      window.clearTimeout(blurTimerRef.current)
      blurTimerRef.current = null
    }
  }

  const handleFocus = () => {
    cancelBlurTimer()
    setIsFocused(true)
  }

  const handleBlur = () => {
    cancelBlurTimer()
    blurTimerRef.current = window.setTimeout(() => {
      setIsFocused(false)
      blurTimerRef.current = null
    }, 120)
  }

  useEffect(() => () => cancelBlurTimer(), [])

  const goPersonalSpace = useCallback(() => {
    sensors.track('web_workgroup_nav_menu_cli', {
      path: '切换个人空间',
      bool1: true,
      groupID: '',
      isGroupVIP: false,
      role: '',
      isAvailable: true,
      track_sign: 'aworkgroup.b4311.action.web_workgroup_nav_menu_cli',
      requirement_id: 608
    })
    window.location.href = PERSONAL_SPACE_PATH
  }, [])

  const changeGroup = useCallback(async (group) => {
    // 获取当前团队ID
    const currentGroupId = localStorage.getItem('group_id')

    // 如果切换的团队与当前团队不同，且当前团队有正在下载的任务，则暂停它们
    if (currentGroupId && currentGroupId !== group.group_id) {
      await pauseAllDownloadingTasks(currentGroupId)
    }

    localStorage.setItem('photoExportDownloadedTasks', 'null') //清除当前团队记录的已下载任务
    localStorage.setItem('photoExportTasks', 'null') //清除当前团队缓存的导出记录
    sessionStorage.setItem('switching_group', 'true')
    localStorage.setItem('group_id', group.group_id)

    // 在个人空间页切换团队时，跳到团队照片页（避免停留在 my-space 路径）
    if (isOnPersonalSpace()) {
      window.location.href = '/photo'
      return
    }

    if (
      /(\/photo\/folder\/detail|\/ai-classify\/folder)\/\d+/.test(
        window.location.pathname
      )
    ) {
      return (window.location.href = '/ai-classify')
    }

    // AI 填表员：切换团队时旧会话不属于新团队，回首页（停留工作页会用旧 sessionId 撞 404）
    if (window.location.pathname.indexOf('/ai/clerk') !== -1) {
      try {
        localStorage.removeItem('aiClerk:conversationId')
      } catch {
        /* localStorage 不可用 */
      }
      window.location.href = '/ai/clerk'
      return
    }

    window.location.reload()
  }, [])

  if (!visible) return null

  return (
    <div className="pop-choose-group-wrap">
      <div className="pop-choose-group">
        <div
          className={`search-group-wrap${
            isFocused ? ' search-group-wrap--focused' : ''
          }`}
        >
          <Input
            className="search-group"
            prefix={<SearchOutlined style={{ color: '#848484' }} />}
            placeholder="搜索"
            value={groupName}
            allowClear
            onChange={(e) => setGroupName(e.target.value.trim())}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
          {!isFocused && (
            <div className="group-action-buttons">
              <button
                type="button"
                className="group-action-btn group-action-btn--join"
                onClick={() => onClickJoin && onClickJoin()}
              >
                加入团队
              </button>
              <button
                type="button"
                className="group-action-btn group-action-btn--create"
                onClick={() => onClickCreate && onClickCreate()}
              >
                创建团队
              </button>
            </div>
          )}
        </div>

        <div className="groups">
          {/* 个人空间固定选项（isOpen=true 时才显示） */}
          {isSpaceOpen && (
            <div
              className="group"
              onClick={goPersonalSpace}
              style={{ borderBottom: '1px solid #f0f0f0' }}
            >
              <div
                className="group-avatar"
                style={{
                  backgroundColor: '#1890ff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 14,
                  fontWeight: 600,
                  color: '#fff',
                  borderRadius: 4,
                  width: 34,
                  height: 34,
                  flexShrink: 0
                }}
              >
                我
              </div>
              <div className="group-name-wrapper">
                <div className="group-name">
                  <span>{nickname}的个人空间</span>
                </div>
                <div className="group-id" style={{ color: '#8c8c8c' }}>
                  仅自己可见
                </div>
              </div>
              {isOnPersonalSpace() && (
                <img className="group-selected" src={groupSelected} />
              )}
            </div>
          )}

          {/* 团队列表 */}
          {(groups || [])
            .filter((group: any) => group.group_name.includes(groupName))
            .map((group: any, index: number) => (
              <div
                key={index}
                className="group"
                onClick={() => changeGroup(group)}
              >
                <GroupAvatar
                  key={index}
                  className="group-avatar"
                  avatar={group.avatar}
                  name={group.group_name}
                  color={group.group_color}
                />
                <div className="group-name-wrapper">
                  <div className="group-name">
                    <span>{group.group_name}</span>
                    {group.is_vip && (
                      <img
                        className="vip-group-icon"
                        src={
                          group.vip_kind === CONFIG.VIP_KIND.VVIP
                            ? vVipGroupIcon
                            : group.vip_kind === CONFIG.VIP_KIND.SVIP
                              ? sVipGroupIcon
                              : vipGroupIcon
                        }
                        alt=""
                      />
                    )}
                  </div>
                  <div className="group-id">
                    团队号：
                    {group.group_lucky_id || group.invite_id}
                  </div>
                </div>
                {curGroup.groupId === group.group_id &&
                  !isOnPersonalSpace() && (
                    <img className="group-selected" src={groupSelected} />
                  )}
              </div>
            ))}
        </div>
      </div>
    </div>
  )
}

interface GroupInfoProps {
  onClickJoin?: () => void
  onClickCreate?: () => void
}

const GroupInfo: React.FC<GroupInfoProps> = ({
  onClickJoin,
  onClickCreate
}) => {
  const group = useAppSelector((state) => state.group)
  const [popChangeGroupVisible, setPopChangeGroupVisible] = useState(false)
  const { isVip, isSVip, isVVip } = useVip()
  const isSpaceOpen = useAppSelector(
    (state: any) => state.mySpace?.personalSpace?.isSpaceOpen ?? false
  )

  const mouseMoveEvent = useCallback((e) => {
    if (e.clientX > 335) {
      setPopChangeGroupVisible(false)
    }
  }, [])
  useEffect(() => {
    window.addEventListener('mousemove', mouseMoveEvent)
    return () => {
      window.removeEventListener('mousemove', mouseMoveEvent)
    }
  }, [])
  return (
    <div
      className="header-group-info"
      onMouseEnter={() => {
        sensors.track('web_workgroup_nav_menu_cli', {
          path: 'switchGroup',
          isGroupVIP: group.isVip,
          bool1: isSpaceOpen
        })
        setPopChangeGroupVisible(true)
      }}
      // onMouseOut={() => setPopChangeGroupVisible(false)}
    >
      <GroupAvatar
        avatar={group.avatar}
        name={group.groupName}
        color={group.color}
        className={'group-avatar'}
      />
      <div className="group-name-wrap">
        <div className="group-name">
          <p>{group.groupName}</p>
          <DownOutlined />
        </div>
        <img
          className="group-vip-badge"
          src={
            isVVip
              ? vVipGroupBadge
              : isSVip
                ? sVipGroupBadge
                : isVip
                  ? vipGroupBadge
                  : notVipGroupBadge
          }
          alt=""
        />
      </div>
      <ChooseGroup
        visible={popChangeGroupVisible}
        onClickJoin={() => {
          setPopChangeGroupVisible(false)
          onClickJoin && onClickJoin()
        }}
        onClickCreate={() => {
          setPopChangeGroupVisible(false)
          onClickCreate && onClickCreate()
        }}
      />
    </div>
  )
}

export default GroupInfo
